import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, MapPin, Landmark, Building2 } from "lucide-react";
import { search as apiSearch } from "@/lib/api";

const typeIcon = (t) => {
  if (["state", "federal_district", "territory"].includes(t)) return <Landmark className="h-4 w-4 text-navy" />;
  if (t === "county") return <Building2 className="h-4 w-4 text-navy" />;
  return <MapPin className="h-4 w-4 text-oldglory-red" />;
};

export function SearchBox({ big = false, testid = "global-search-input" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = setTimeout(() => {
      if (q.trim().length >= 1) {
        apiSearch(q.trim()).then((d) => { setResults(d.results); setOpen(true); setActive(0); }).catch(() => {});
      } else {
        setResults([]); setOpen(false);
      }
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r) => {
    setOpen(false); setQ("");
    navigate(`/place${r.url_path}`);
  };

  const submit = () => {
    if (results[active]) go(results[active]);
    else if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") submit();
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={box} className="relative w-full">
      <div className="flex items-stretch overflow-hidden rounded-lg border border-navy/20 bg-white shadow-sm focus-within:border-navy transition-colors">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search a state, county, city, town, ZIP code or community…"
          className={`w-full bg-transparent px-4 outline-none placeholder:text-muted-foreground/70 ${big ? "py-4 text-base" : "py-2.5 text-sm"}`}
          data-testid={testid}
        />
        <button onClick={submit} className="flex items-center justify-center bg-navy px-5 text-white transition-colors hover:bg-navy-800" data-testid="search-submit-btn">
          <SearchIcon className="h-5 w-5" />
        </button>
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-lg border border-border bg-white shadow-xl" data-testid="search-results-dropdown">
          {results.map((r, i) => (
            <button
              key={r.geo_id}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(r)}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === active ? "bg-parchment" : "hover:bg-parchment/60"}`}
              data-testid={`search-result-${r.geo_id}`}
            >
              {typeIcon(r.geo_type)}
              <span className="flex-1">
                <span className="font-medium text-ink">{r.name}</span>
                {r.state_name && !["state", "federal_district", "territory"].includes(r.geo_type) && (
                  <span className="ml-2 text-xs text-muted-foreground">{r.state_name}</span>
                )}
                {!r.state_name && r.nickname && <span className="ml-2 text-xs text-muted-foreground">{r.nickname}</span>}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.type_label || r.geo_type.replace("_", " ")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
